import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { contactsService } from '../../../services/partnerService';

const ContactsDirectory = ({ partnerId }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  useEffect(() => {
    if (partnerId) loadContacts();
  }, [partnerId]);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const data = await contactsService?.getByPartner(partnerId);
      setContacts(data || []);
    } catch (err) {
      console.error('Error loading contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const roles = ['all', ...new Set(contacts?.map(c => c?.role)?.filter(Boolean))];

  const filteredContacts = contacts?.filter((contact) => {
    const matchesSearch =
      contact?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      contact?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      contact?.role?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = filterRole === 'all' || contact?.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const getRoleBadgeColor = (role) => {
    const colors = {
      'Primary Contact': 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white',
      'Technical Lead': 'bg-gradient-to-r from-purple-500 to-pink-500 text-white',
      'Finance Contact': 'bg-gradient-to-r from-green-500 to-emerald-500 text-white',
      'Sales Contact': 'bg-gradient-to-r from-orange-500 to-amber-500 text-white',
      'Legal Contact': 'bg-gradient-to-r from-slate-600 to-slate-700 text-white'
    };
    return colors?.[role] || 'bg-gradient-to-r from-primary to-secondary text-white';
  };

  const getInitials = (name) => {
    return name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2) || '?';
  };

  if (loading) {
    return (
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-32">
        <div className="flex items-center gap-12 mb-24">
          <div className="p-12 bg-gradient-to-br from-primary to-secondary rounded-xl">
            <Icon name="Users" size={24} className="text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Contacts Directory</h2>
            <p className="text-sm text-slate-600">All registered contacts with role assignments</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-48">
          <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-32">
      <div className="flex items-center justify-between mb-24">
        <div className="flex items-center gap-12">
          <div className="p-12 bg-gradient-to-br from-primary to-secondary rounded-xl">
            <Icon name="Users" size={24} className="text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Contacts Directory</h2>
            <p className="text-sm text-slate-600">{contacts?.length} registered contact{contacts?.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-16 mb-24">
        <div className="flex-1 relative">
          <Icon name="Search" size={18} className="absolute left-16 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search contacts by name, email, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-48 pr-16 py-12 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-smooth"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e?.target?.value)}
          className="px-16 py-12 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-smooth"
        >
          {roles?.map((role) => (
            <option key={role} value={role}>
              {role === 'all' ? 'All Roles' : role}
            </option>
          ))}
        </select>
      </div>

      {/* Contacts Grid */}
      {filteredContacts?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
          {filteredContacts?.map((contact) => (
            <div key={contact?.id} className="bg-white rounded-xl border border-slate-200 p-24 hover:shadow-elevation-md transition-smooth">
              <div className="flex items-start gap-16 mb-20">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-lg shadow-md">
                    {getInitials(contact?.name)}
                  </div>
                  {contact?.is_primary && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full border-2 border-white flex items-center justify-center">
                      <Icon name="Check" size={12} className="text-white" />
                    </div>
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex items-start justify-between mb-8">
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-4">{contact?.name}</h4>
                      {contact?.is_primary && (
                        <p className="text-xs text-success font-medium">Primary Contact</p>
                      )}
                    </div>
                  </div>
                  {contact?.role && (
                    <span className={`inline-block px-12 py-4 rounded-full text-xs font-semibold ${getRoleBadgeColor(contact?.role)}`}>
                      {contact?.role}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-12 mb-20">
                {contact?.email && (
                  <div className="flex items-center gap-12 text-sm">
                    <Icon name="Mail" size={16} className="text-slate-400" />
                    <a href={`mailto:${contact?.email}`} className="text-primary hover:text-secondary transition-smooth truncate">
                      {contact?.email}
                    </a>
                  </div>
                )}
                {contact?.phone && (
                  <div className="flex items-center gap-12 text-sm">
                    <Icon name="Phone" size={16} className="text-slate-400" />
                    <a href={`tel:${contact?.phone}`} className="text-slate-700 hover:text-slate-900 transition-smooth">
                      {contact?.phone}
                    </a>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-8 pt-20 border-t border-slate-100">
                {contact?.email && (
                  <a
                    href={`mailto:${contact?.email}`}
                    className="flex-1 px-16 py-10 bg-primary/10 text-primary rounded-lg text-sm font-medium hover:bg-primary/20 transition-smooth flex items-center justify-center gap-8"
                  >
                    <Icon name="Mail" size={16} />
                    Email
                  </a>
                )}
                {contact?.phone && (
                  <a
                    href={`tel:${contact?.phone}`}
                    className="flex-1 px-16 py-10 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-smooth flex items-center justify-center gap-8"
                  >
                    <Icon name="Phone" size={16} />
                    Call
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-48">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-16">
            <Icon name="Users" size={32} className="text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-8">
            {contacts?.length === 0 ? 'No contacts added yet' : 'No contacts found'}
          </h3>
          <p className="text-sm text-slate-600">
            {contacts?.length === 0
              ? 'Contacts added during registration will appear here.' :'Try adjusting your search or filter criteria'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ContactsDirectory;